import Enums from "@/enums";

const FIELD_ELEMENT_TO_KEY = {
    "first name": "firstName",
    "last name": "lastName",
    "company name": "company",
    "recruiter initials": "initials",
    position: "position",
    location: "location",
    "location selection": "siteId",
    "site select": "siteId",
    job: "jobId",
    "job select": "jobId",
    initials: "initials",
    supervisor: "reportRecipient",
    "report recipient": "reportRecipient",
    gender: "gender",
    email: "email",
    "birth city": "birthCity",
    "birth country": "birthCountry",
    "birth initials": "birthInitials",
    "billing source": "billingSource",
    facility: "facility",
    uniqueid: "uniqueId",
    charge: "charge",
    "parole officer": "paroleOfficer",
    internal: "internal",
    "custom pick list": "customPickList",
};

function normalizeFieldKey(value) {
    return String(value || "").trim().toLowerCase();
}

function mapFieldElementToKey(fieldElementName) {
    return FIELD_ELEMENT_TO_KEY[normalizeFieldKey(fieldElementName)] || "";
}

function normalizeValue(value) {
    if (value === null || value === undefined) return "";
    return String(value);
}

function resolveJobDisplayValue(testState, testRecord) {
    const directJob = normalizeValue(testRecord.job);
    if (directJob) return directJob;

    const positionText = normalizeValue(testRecord.position);
    if (positionText) return positionText;

    const jobId = Number(testRecord.jobId ?? testRecord.companyJobId ?? 0);
    if (!jobId) return "";

    const jobLists = [testState?.jobs, testState?.testTypes?.[0]?.jobs].filter(Array.isArray);
    for (const jobs of jobLists) {
        const job = jobs.find((j) => Number(j.id ?? j.jobId) === jobId);
        const name = normalizeValue(job?.name || job?.jobName);
        if (name) return name;
    }

    const efMatch = (testState?.efTests || []).find((ef) => Number(ef.jobId) === jobId);
    if (efMatch) {
        return normalizeValue(efMatch.jobName || efMatch.job);
    }

    const siteJobMatch = (testState?.siteJobs || []).find(
        (sj) => Number(sj.jobId ?? sj.companyJobId) === jobId
    );
    if (siteJobMatch) {
        return normalizeValue(siteJobMatch.jobName || siteJobMatch.name);
    }

    return "";
}

function resolveFieldValue(testState, field) {
    const testRecord = testState?.testData || {};
    const elementName = normalizeValue(field?.fieldElementName || field?.name);
    const normalizedElement = normalizeFieldKey(elementName);
    const key = mapFieldElementToKey(elementName);

    if (
        key === "siteId" ||
        normalizedElement === "site select" ||
        normalizedElement === "location selection"
    ) {
        const siteId = Number(testRecord.siteId || 0);
        const sites =
            testState?.sites ||
            testState?.testTypes?.[0]?.sites ||
            [];
        const site = sites.find((s) => Number(s.id ?? s.siteId) === siteId);
        return normalizeValue(site?.name || site?.alias || testRecord.location);
    }

    if (
        key === "jobId" ||
        normalizedElement === "job" ||
        normalizedElement === "job select"
    ) {
        return resolveJobDisplayValue(testState, testRecord);
    }

    if (normalizedElement === "custom") {
        const label = normalizeValue(field?.label);
        return normalizeValue(testRecord.custom?.[label]);
    }

    if (key) {
        return normalizeValue(testRecord[key]);
    }

    return "";
}

function getCttFieldsForBar(testState) {
    const testTypes = Array.isArray(testState?.testTypes) ? testState.testTypes : [];
    const cttId = Number(
        testState?.testData?.companyTestTypeId ??
        testState?.testData?.CompanyTestTypeId ??
        testTypes[0]?.companyTestType?.id ??
        0
    );

    if (cttId > 0) {
        const matchedType = testTypes.find((tt) => Number(tt?.companyTestType?.id) === cttId);
        if (matchedType?.fields?.length) {
            return matchedType.fields;
        }
    }

    return testTypes[0]?.fields ?? [];
}

/** Only merge live form values for proctor-provided fields (not testee request fields). */
function pickProctorProvidedValueOverrides(cttFields, valueOverrides) {
    if (!valueOverrides || typeof valueOverrides !== "object") {
        return null;
    }

    const proctorFields = cttFields.filter((field) => field?.proctorProvided);
    if (!proctorFields.length) {
        return null;
    }

    const picked = {};
    let hasAny = false;

    for (const field of proctorFields) {
        const elementName = normalizeFieldKey(field?.fieldElementName || field?.name);

        if (elementName === "custom") {
            const label = normalizeValue(field.label);
            const customValue = valueOverrides.custom?.[label];
            if (normalizeValue(customValue)) {
                if (!picked.custom) {
                    picked.custom = {};
                }
                picked.custom[label] = customValue;
                hasAny = true;
            }
            continue;
        }

        const dataKey = mapFieldElementToKey(field?.fieldElementName || field?.name);
        if (!dataKey) {
            continue;
        }

        const value = valueOverrides[dataKey];
        if (normalizeValue(value)) {
            picked[dataKey] = value;
            hasAny = true;
        }
    }

    return hasAny ? picked : null;
}

function mergeProctorProvidedTestData(testData, proctorOverrides) {
    if (!proctorOverrides) {
        return testData || {};
    }

    return {
        ...(testData || {}),
        ...proctorOverrides,
        custom: {
            ...(testData?.custom || {}),
            ...(proctorOverrides.custom || {}),
        },
    };
}

export function isBeSourceTest(testState) {
    const testTypes = Array.isArray(testState?.testTypes) ? testState.testTypes : [];
    const cttId = Number(
        testState?.testData?.companyTestTypeId ??
        testState?.testData?.CompanyTestTypeId ??
        testTypes[0]?.companyTestType?.id ??
        0
    );
    const matchedType =
        cttId > 0
            ? testTypes.find((tt) => Number(tt?.companyTestType?.id) === cttId)
            : testTypes[0];
    const evalComparison = matchedType?.companyTestType?.evalComparison;
    return Number(evalComparison) === Enums.TEST_EVAL_COMPARISON.BE_SOURCE;
}

function mapCttFieldsToRows(cttFields, effectiveTestState) {
    const translatedFields = effectiveTestState?.fields || [];
    const translatedByElement = new Map(
        translatedFields.map((field) => [normalizeFieldKey(field.fieldElementName), field])
    );

    return cttFields
        .filter((field) => normalizeFieldKey(field.fieldElementName) !== "language")
        .slice()
        .sort((a, b) => Number(a.sequence || 0) - Number(b.sequence || 0))
        .map((field) => {
            const translated = translatedByElement.get(normalizeFieldKey(field.fieldElementName));
            const label = normalizeValue(translated?.label || field.label || field.fieldElementName);
            const value = resolveFieldValue(effectiveTestState, field);
            return {
                id: field.id ?? `${label}-${field.sequence || 0}`,
                label,
                value,
            };
        })
        .filter((row) => row.label);
}

/**
 * @param {Record<string, unknown> | null | undefined} testState
 * @param {Record<string, unknown> | null} valueOverrides Live request-form values (request page only).
 * @param {'request' | 'inTest'} mode `request` — proctor-provided fields only; `inTest` — any field with a value.
 * @returns {{ id: string|number, label: string, value: string }[]}
 */
export function buildProctorProvidedFieldRows(testState, valueOverrides = null, mode = "request") {
    if (!isBeSourceTest(testState)) {
        return [];
    }

    const cttFields = getCttFieldsForBar(testState);

    if (mode === "inTest") {
        const effectiveTestState = { ...testState, testData: testState?.testData || {} };
        return mapCttFieldsToRows(cttFields, effectiveTestState).filter((row) =>
            normalizeValue(row.value)
        );
    }

    const proctorOverrides = pickProctorProvidedValueOverrides(cttFields, valueOverrides);
    const effectiveTestState = {
        ...testState,
        testData: mergeProctorProvidedTestData(testState?.testData, proctorOverrides),
    };

    return mapCttFieldsToRows(
        cttFields.filter((field) => field?.proctorProvided),
        effectiveTestState
    );
}
